/**
 * Audit Logging Utility for HIPAA Compliance
 * 
 * This module provides audit logging functionality to track all access
 * to Protected Health Information (PHI) and sensitive data.
 * 
 * Logs are immutable and stored in a separate Firestore collection.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getAuth } from 'firebase/auth';

// Action types for audit logging
export const AUDIT_ACTIONS = {
  // Read operations
  READ: 'READ',
  READ_LIST: 'READ_LIST',
  SEARCH: 'SEARCH',
  EXPORT: 'EXPORT',
  
  // Write operations
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  
  // Auth operations
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  LOGIN_FAILED: 'LOGIN_FAILED',
  PASSWORD_RESET: 'PASSWORD_RESET',
  
  // PHI-specific operations
  PHI_ACCESS: 'PHI_ACCESS',
  PHI_EXPORT: 'PHI_EXPORT',
  PHI_PRINT: 'PHI_PRINT',
  
  // Admin operations
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_DEACTIVATED: 'USER_DEACTIVATED',
  ROLE_CHANGED: 'ROLE_CHANGED',
  PERMISSION_GRANTED: 'PERMISSION_GRANTED',
  PERMISSION_REVOKED: 'PERMISSION_REVOKED'
};

// Data classification levels
export const DATA_CLASSIFICATION = {
  PHI: 'PHI',           // Protected Health Information
  PII: 'PII',           // Personally Identifiable Information
  SENSITIVE: 'SENSITIVE', // Sensitive business data
  INTERNAL: 'INTERNAL',   // Internal use only
  PUBLIC: 'PUBLIC'        // Public data
};

/**
 * Creates an audit log entry
 * @param {Object} logData - The audit log data
 * @param {string} logData.action - The action performed (from AUDIT_ACTIONS)
 * @param {string} logData.collection - The Firestore collection accessed
 * @param {string} logData.documentId - The document ID accessed (if applicable)
 * @param {string} logData.classification - Data classification level
 * @param {string} logData.description - Human-readable description
 * @param {Object} logData.metadata - Additional metadata
 * @returns {Promise<string>} - The created audit log document ID
 */
export const createAuditLog = async (logData) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    const auditEntry = {
      // User information
      userId: user?.uid || 'anonymous',
      userEmail: user?.email?.toLowerCase() || 'unknown',
      
      // Action details
      action: logData.action || AUDIT_ACTIONS.READ,
      collection: logData.collection || 'unknown',
      documentId: logData.documentId || null,
      
      // Classification
      classification: logData.classification || DATA_CLASSIFICATION.INTERNAL,
      
      // Description
      description: logData.description || '',
      
      // Organization (multi-tenancy)
      organization: logData.organization || 'alliedoms',
      
      // Metadata
      metadata: {
        ...logData.metadata,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
        timestamp: new Date().toISOString()
      },
      
      // System fields
      createdAt: serverTimestamp(),
      
      // Session info (if available)
      sessionId: getSessionId()
    };
    
    const auditRef = await addDoc(collection(db, 'auditLog'), auditEntry);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[AUDIT]', auditEntry.action, auditEntry.collection, auditEntry.documentId);
    }
    
    return auditRef.id;
  } catch (error) {
    // Don't throw - audit logging should not break the application
    console.error('Failed to create audit log:', error);
    return null;
  }
};

/**
 * Logs a PHI access event
 * @param {string} collectionName - The collection containing PHI
 * @param {string} documentId - The document ID accessed
 * @param {string} action - The action performed
 * @param {Object} additionalData - Any additional context
 */
export const logPHIAccess = async (collectionName, documentId, action = AUDIT_ACTIONS.PHI_ACCESS, additionalData = {}) => {
  return createAuditLog({
    action,
    collection: collectionName,
    documentId,
    classification: DATA_CLASSIFICATION.PHI,
    description: `PHI ${action.toLowerCase()} in ${collectionName}`,
    metadata: {
      ...additionalData,
      phiAccess: true
    }
  });
};

/**
 * Logs a form submission event
 * @param {string} formType - The type of form submitted
 * @param {string} documentId - The created document ID
 * @param {boolean} containsPHI - Whether the form contains PHI
 */
export const logFormSubmission = async (formType, documentId, containsPHI = true) => {
  return createAuditLog({
    action: AUDIT_ACTIONS.CREATE,
    collection: formType,
    documentId,
    classification: containsPHI ? DATA_CLASSIFICATION.PHI : DATA_CLASSIFICATION.INTERNAL,
    description: `Form submission: ${formType}`,
    metadata: {
      formType,
      containsPHI
    }
  });
};

/**
 * Logs a login event
 * @param {boolean} success - Whether login was successful
 * @param {string} email - The email attempted
 * @param {string} reason - Failure reason (if applicable)
 */
export const logLogin = async (success, email, reason = null) => {
  return createAuditLog({
    action: success ? AUDIT_ACTIONS.LOGIN : AUDIT_ACTIONS.LOGIN_FAILED,
    collection: 'authentication',
    documentId: null,
    classification: DATA_CLASSIFICATION.INTERNAL,
    description: success ? `Successful login for ${email}` : `Failed login attempt for ${email}`,
    metadata: {
      email: email?.toLowerCase(),
      success,
      failureReason: reason
    }
  });
};

/**
 * Logs a logout event
 */
export const logLogout = async () => {
  return createAuditLog({
    action: AUDIT_ACTIONS.LOGOUT,
    collection: 'authentication',
    documentId: null,
    classification: DATA_CLASSIFICATION.INTERNAL,
    description: 'User logged out'
  });
};

/**
 * Logs a data export event
 * @param {string} collectionName - The collection being exported
 * @param {number} recordCount - Number of records exported
 * @param {boolean} containsPHI - Whether export contains PHI
 */
export const logDataExport = async (collectionName, recordCount, containsPHI = true) => {
  return createAuditLog({
    action: containsPHI ? AUDIT_ACTIONS.PHI_EXPORT : AUDIT_ACTIONS.EXPORT,
    collection: collectionName,
    documentId: null,
    classification: containsPHI ? DATA_CLASSIFICATION.PHI : DATA_CLASSIFICATION.INTERNAL,
    description: `Exported ${recordCount} records from ${collectionName}`,
    metadata: {
      recordCount,
      containsPHI
    }
  });
};

/**
 * Logs an admin action
 * @param {string} action - The admin action performed
 * @param {string} targetUserId - The user affected by the action
 * @param {Object} details - Additional details
 */
export const logAdminAction = async (action, targetUserId, details = {}) => {
  return createAuditLog({
    action,
    collection: 'users',
    documentId: targetUserId,
    classification: DATA_CLASSIFICATION.SENSITIVE,
    description: `Admin action: ${action} on user ${targetUserId}`,
    metadata: {
      targetUserId,
      ...details
    }
  });
};

/**
 * Gets or creates a session ID for tracking user sessions
 * @returns {string} - Session ID
 */
const getSessionId = () => {
  if (typeof window === 'undefined') return 'server-session';
  
  let sessionId = sessionStorage.getItem('auditSessionId');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('auditSessionId', sessionId);
  }
  return sessionId;
};

/**
 * Wrapper for Firestore operations that automatically logs access
 * @param {Function} operation - The Firestore operation to perform
 * @param {Object} logConfig - Configuration for the audit log
 * @returns {Promise} - The result of the operation
 */
export const withAuditLog = async (operation, logConfig) => {
  const startTime = Date.now();
  
  try {
    const result = await operation();
    
    // Log successful operation
    await createAuditLog({
      ...logConfig,
      metadata: {
        ...logConfig.metadata,
        duration: Date.now() - startTime,
        success: true
      }
    });
    
    return result;
  } catch (error) {
    // Log failed operation
    await createAuditLog({
      ...logConfig,
      metadata: {
        ...logConfig.metadata,
        duration: Date.now() - startTime,
        success: false,
        error: error.message
      }
    });
    
    throw error;
  }
};

export default {
  createAuditLog,
  logPHIAccess,
  logFormSubmission,
  logLogin,
  logLogout,
  logDataExport,
  logAdminAction,
  withAuditLog,
  AUDIT_ACTIONS,
  DATA_CLASSIFICATION
};
